import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Progress,
  Tooltip,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Badge,
  Tabs,
  Checkbox,
  Radio,
  Spin,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  StopOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  RocketOutlined,
  DashboardOutlined,
  BarChartOutlined,
  ApiOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  getProgrammaticSeoCampaigns,
  createProgrammaticSeoCampaign,
  updateProgrammaticSeoCampaign,
  deleteProgrammaticSeoCampaign,
  startProgrammaticSeoCampaign,
  pauseProgrammaticSeoCampaign,
  stopProgrammaticSeoCampaign,
  getProgrammaticSeoCampaignProgress,
} from "@api/seo/programmaticSeo";
import CampaignDataSelector from "../../../../components/Forms/programmaticSeo/CampaignForm/CampaignDataSelector";
import "./ProgrammaticSeoCampaigns.css";

const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const ProgrammaticSeoCampaigns = () => {
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [filters, setFilters] = useState({
    status: null,
    pageType: null,
    searchText: "",
  });
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [progressModalVisible, setProgressModalVisible] = useState(false);
  const [campaignProgress, setCampaignProgress] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  useEffect(() => {
    fetchCampaigns();
  }, [pagination, filters]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await getProgrammaticSeoCampaigns({
        page: pagination.current,
        perPage: pagination.pageSize,
        ...filters,
      });

      setCampaigns(response.data.data.campaigns || []);
      setTotal(response.data.data.total || 0);
      setStats(response.data.data.stats || {});
    } catch (error) {
      message.error("Failed to fetch campaigns");
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const handleCreateCampaign = async (values) => {
    try {
      setLoading(true);

      // Format dates
      values.scheduledStart = values.dateRange[0].toISOString();
      values.scheduledEnd = values.dateRange[1].toISOString();
      delete values.dateRange;

      await createProgrammaticSeoCampaign(values);
      message.success("Campaign created successfully");
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchCampaigns();
    } catch (error) {
      message.error(
        error.response?.data?.message || "Failed to create campaign"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCampaign = async (values) => {
    try {
      setLoading(true);

      if (values.dateRange) {
        values.scheduledStart = values.dateRange[0].toISOString();
        values.scheduledEnd = values.dateRange[1].toISOString();
        delete values.dateRange;
      }

      await updateProgrammaticSeoCampaign(selectedCampaign._id, values);
      message.success("Campaign updated successfully");
      setEditModalVisible(false);
      editForm.resetFields();
      fetchCampaigns();
    } catch (error) {
      message.error(
        error.response?.data?.message || "Failed to update campaign"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await deleteProgrammaticSeoCampaign(campaignId);
      message.success("Campaign deleted successfully");
      fetchCampaigns();
    } catch (error) {
      message.error(
        error.response?.data?.message || "Failed to delete campaign"
      );
    }
  };

  const handleStartCampaign = async (campaignId) => {
    try {
      setActionLoading(prev => ({ ...prev, [campaignId]: 'start' }));
      await startProgrammaticSeoCampaign(campaignId);
      message.success("Campaign started successfully");
      fetchCampaigns();
    } catch (error) {
      message.error(
        error.response?.data?.message || "Failed to start campaign"
      );
    } finally {
      setActionLoading(prev => ({ ...prev, [campaignId]: null }));
    }
  };

  const handlePauseCampaign = async (campaignId) => {
    try {
      setActionLoading(prev => ({ ...prev, [campaignId]: 'pause' }));
      await pauseProgrammaticSeoCampaign(campaignId);
      message.success("Campaign paused successfully");
      fetchCampaigns();
    } catch (error) {
      message.error(
        error.response?.data?.message || "Failed to pause campaign"
      );
    } finally {
      setActionLoading(prev => ({ ...prev, [campaignId]: null }));
    }
  };

  const handleStopCampaign = async (campaignId) => {
    try {
      setActionLoading(prev => ({ ...prev, [campaignId]: 'stop' }));
      await stopProgrammaticSeoCampaign(campaignId);
      message.success("Campaign stopped successfully");
      fetchCampaigns();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to stop campaign");
    } finally {
      setActionLoading(prev => ({ ...prev, [campaignId]: null }));
    }
  };

  const handleViewProgress = async (campaign) => {
    try {
      const response = await getProgrammaticSeoCampaignProgress(campaign._id);
      setCampaignProgress(response.data.data);
      setSelectedCampaign(campaign);
      setProgressModalVisible(true);
    } catch (error) {
      message.error("Failed to fetch campaign progress");
    }
  };

  const columns = [
    {
      title: "Campaign Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Space direction="vertical" size="small">
          <a
            style={{ fontWeight: 500 }}
            onClick={() => handleViewProgress(record)}
          >
            {text}
          </a>
          {record.description && (
            <span style={{ color: "rgba(0, 0, 0, 0.45)", fontSize: "12px" }}>
              {record.description.substring(0, 50)}...
            </span>
          )}
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "pageType",
      key: "pageType",
      width: 120,
      filters: [
        { text: "AI Copilot", value: "copilotType" },
        { text: "Industry Solutions", value: "industry" },
        { text: "Professional Tasks", value: "professionalTask" },
        { text: "Location Services", value: "location" },
        { text: "Location + Copilot", value: "locationCopilot" },
        { text: "Industry + Copilot", value: "industryCopilot" },
        { text: "Task + Copilot", value: "taskCopilot" },
        { text: "Comparison", value: "comparison" },
      ],
      render: (type) => {
        const typeConfig = {
          copilotType: { color: "blue", text: "AI Copilot" },
          industry: { color: "green", text: "Industry Solutions" },
          professionalTask: { color: "purple", text: "Professional Tasks" },
          location: { color: "orange", text: "Location Services" },
          locationCopilot: { color: "geekblue", text: "Location + Copilot" },
          industryCopilot: { color: "cyan", text: "Industry + Copilot" },
          taskCopilot: { color: "magenta", text: "Task + Copilot" },
          comparison: { color: "red", text: "Comparison" },
        };
        const config = typeConfig[type] || { color: "default", text: type };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      filters: [
        { text: "Scheduled", value: "scheduled" },
        { text: "Running", value: "running" },
        { text: "Paused", value: "paused" },
        { text: "Completed", value: "completed" },
      ],
      render: (status) => {
        const statusConfig = {
          draft: { color: "default", text: "Draft" },
          scheduled: { color: "blue", text: "Scheduled" },
          running: { color: "processing", text: "Running" },
          paused: { color: "warning", text: "Paused" },
          completed: { color: "success", text: "Completed" },
          failed: { color: "error", text: "Failed" },
        };
        const config = statusConfig[status] || statusConfig.draft;
        return <Badge status={config.color} text={config.text} />;
      },
    },
    {
      title: "Progress",
      key: "progress",
      width: 200,
      render: (_, record) => {
        const progress =
          record.totalPages > 0
            ? Math.round((record.pagesGenerated / record.totalPages) * 100)
            : 0;
        return (
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Progress
              percent={progress}
              size="small"
              status={
                record.status === "completed"
                  ? "success"
                  : record.status === "failed"
                  ? "exception"
                  : "active"
              }
            />
            <span style={{ fontSize: "12px", color: "rgba(0, 0, 0, 0.45)" }}>
              {record.pagesGenerated} / {record.totalPages} pages
            </span>
          </Space>
        );
      },
    },
    {
      title: "Schedule",
      key: "schedule",
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <span style={{ fontSize: "12px" }}>
            <CalendarOutlined style={{ marginRight: 4 }} />
            {moment(record.scheduledStart).format("MMM DD, YYYY")}
          </span>
          <span style={{ fontSize: "12px", color: "rgba(0, 0, 0, 0.45)" }}>
            to {moment(record.scheduledEnd).format("MMM DD, YYYY")}
          </span>
          {record.timeDistribution === "custom" && (
            <Tooltip title={`${record.customInterval} minute(s) between pages`}>
              <Tag color="blue" style={{ fontSize: "10px", marginTop: 4 }}>
                <ClockCircleOutlined /> {record.customInterval}min interval
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      sorter: true,
      render: (date) => moment(date).format("MMM DD, YYYY"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      fixed: "right",
      render: (_, record) => {
        const isRunning = record.status === "running";
        const isPaused = record.status === "paused";
        const isScheduled = record.status === "scheduled";
        const isCompleted = record.status === "completed";

        const currentLoading = actionLoading[record._id];
        const isAnyActionLoading = currentLoading !== null && currentLoading !== undefined;

        return (
          <Space>
            {isScheduled && (
              <Tooltip title="Start Campaign">
                <Button
                  type="text"
                  size="small"
                  icon={<PlayCircleOutlined />}
                  onClick={() => handleStartCampaign(record._id)}
                  style={{ color: "#52c41a" }}
                  loading={currentLoading === 'start'}
                  disabled={isAnyActionLoading}
                />
              </Tooltip>
            )}
            {isRunning && (
              <Tooltip title="Pause Campaign">
                <Button
                  type="text"
                  size="small"
                  icon={<PauseCircleOutlined />}
                  onClick={() => handlePauseCampaign(record._id)}
                  style={{ color: "#faad14" }}
                  loading={currentLoading === 'pause'}
                  disabled={isAnyActionLoading}
                />
              </Tooltip>
            )}
            {isPaused && (
              <Tooltip title="Resume Campaign">
                <Button
                  type="text"
                  size="small"
                  icon={<PlayCircleOutlined />}
                  onClick={() => handleStartCampaign(record._id)}
                  style={{ color: "#52c41a" }}
                  loading={currentLoading === 'start'}
                  disabled={isAnyActionLoading}
                />
              </Tooltip>
            )}
            {(isRunning || isPaused) && (
              <Tooltip title="Stop Campaign">
                <Button
                  type="text"
                  size="small"
                  icon={<StopOutlined />}
                  onClick={() => handleStopCampaign(record._id)}
                  danger
                  loading={currentLoading === 'stop'}
                  disabled={isAnyActionLoading}
                />
              </Tooltip>
            )}
            {(isScheduled || isPaused) && (
              <Tooltip title="Edit Campaign">
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setSelectedCampaign(record);
                    editForm.setFieldsValue({
                      ...record,
                      dateRange: [
                        moment(record.scheduledStart),
                        moment(record.scheduledEnd),
                      ],
                    });
                    setEditModalVisible(true);
                  }}
                />
              </Tooltip>
            )}
            {!isRunning && (
              <Popconfirm
                title="Are you sure you want to delete this campaign?"
                onConfirm={() => handleDeleteCampaign(record._id)}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip title="Delete Campaign">
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination(pagination);
    setFilters((prev) => ({
      ...prev,
      status: filters.status?.[0] || null,
      pageType: filters.pageType?.[0] || null,
    }));
  };

  const navigateTo = (path) => {
    const basePath =
      user?.role === "superadmin" ? "/super-admin" : "/marketing-admin";
    history.push(`${basePath}${path}`);
  };

  // Track initial load separately from table loading
  const [initialLoad, setInitialLoad] = useState(true);

  return (
    <div className="programmatic-seo-campaigns">
      {/* Header Section */}
      <div className="seo-dashboard-header">
        <Row justify="space-between" align="middle">
          <Col>
            <h1>Programmatic SEO Campaigns</h1>
            <p>
              Create and manage AI-powered SEO campaigns with intelligent scheduling
            </p>
            <div className="seo-nav-pills">
              <Button
                icon={<DashboardOutlined />}
                onClick={() => navigateTo("/seo/programmatic-seo/dashboard")}
              >
                Dashboard
              </Button>
              <Button type="primary" icon={<RocketOutlined />}>
                Campaigns
              </Button>
              <Button
                icon={<BarChartOutlined />}
                onClick={() => navigateTo("/seo/programmatic-seo/analytics")}
              >
                Analytics
              </Button>
              <Button
                icon={<FileTextOutlined />}
                onClick={() => navigateTo("/seo/programmatic-seo/pages")}
              >
                Pages
              </Button>
            </div>
          </Col>
          <Col>
            <Space className="action-button-group">
              <Button
                className="seo-action-btn seo-action-btn-secondary"
                icon={<ApiOutlined />}
                onClick={() => navigateTo("/seo/ai-apps")}
              >
                AI Apps
              </Button>
              <Button
                className="seo-action-btn seo-action-btn-secondary"
                icon={<GlobalOutlined />}
                onClick={() => navigateTo("/seo/seo-glossary")}
              >
                SEO Glossary
              </Button>
              <Button
                className="seo-action-btn seo-action-btn-secondary"
                icon={<ReloadOutlined />}
                onClick={fetchCampaigns}
                loading={loading}
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <div className="seo-dashboard-content">
        <Spin spinning={initialLoad} size="large" tip="Loading campaigns...">
          {/* Search and Filters */}
          <Card className="filter-section" style={{ marginBottom: 24 }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Space>
                  <Input.Search
                    placeholder="Search campaigns..."
                    allowClear
                    onSearch={(value) =>
                      setFilters((prev) => ({ ...prev, searchText: value }))
                    }
                    style={{ width: 300 }}
                  />
                </Space>
              </Col>
              <Col>
                <Space>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModalVisible(true)}
                    className="seo-action-btn seo-action-btn-primary"
                  >
                    Create Campaign
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Campaign Statistics */}
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <Card className="stat-card">
                <Statistic
                  title="Total Campaigns"
                  value={total}
                  prefix={<CalendarOutlined className="icon-calendar" />}
                  formatter={(value) => value.toLocaleString()}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card className="stat-card">
                <Statistic
                  title="Running"
                  value={stats.running || 0}
                  valueStyle={{ color: "#667eea" }}
                  prefix={<RocketOutlined className="icon-rocket" />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card className="stat-card">
                <Statistic
                  title="Scheduled"
                  value={stats.scheduled || 0}
                  valueStyle={{ color: "#10b981" }}
                  prefix={<ClockCircleOutlined className="icon-clock" />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card className="stat-card">
                <Statistic
                  title="Completed"
                  value={stats.completed || 0}
                  valueStyle={{ color: "#3f8600" }}
                  prefix={<FileTextOutlined className="icon-file" />}
                />
              </Card>
            </Col>
          </Row>

          {/* Campaigns Table */}
          <Card className="table-card">
            <Table
              columns={columns}
              dataSource={campaigns}
              rowKey="_id"
              loading={loading}
              pagination={{
                ...pagination,
                total,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} campaigns`,
              }}
              onChange={handleTableChange}
              scroll={{ x: 1400 }}
            />
          </Card>
        </Spin>
      </div>

      {/* Create Campaign Modal */}
      <Modal
        title="Create Programmatic SEO Campaign"
        visible={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateCampaign}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Campaign Name"
                rules={[
                  { required: true, message: "Please enter campaign name" },
                ]}
              >
                <Input placeholder="e.g., Q1 2024 Comparison Pages" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="pageType"
                label="Page Type"
                rules={[{ required: true, message: "Please select page type" }]}
              >
                <Select placeholder="Select page type">
                  <Option value="copilotType">AI Copilot</Option>
                  <Option value="industry">Industry Solutions</Option>
                  <Option value="professionalTask">Professional Tasks</Option>
                  <Option value="location">Location Services</Option>
                  <Option value="locationCopilot">Location + Copilot</Option>
                  <Option value="industryCopilot">Industry + Copilot</Option>
                  <Option value="taskCopilot">Task + Copilot</Option>
                  <Option value="comparison">Comparison</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea
              rows={3}
              placeholder="Describe the purpose of this campaign..."
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="totalPages"
                label="Total Pages to Generate"
                rules={[
                  { required: true, message: "Please enter total pages" },
                ]}
              >
                <InputNumber
                  min={1}
                  max={10000}
                  style={{ width: "100%" }}
                  placeholder="e.g., 100"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="pagesPerBatch"
                label="Pages per Batch"
                initialValue={10}
              >
                <InputNumber
                  min={1}
                  max={100}
                  style={{ width: "100%" }}
                  placeholder="e.g., 10"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="dateRange"
                label="Schedule Period"
                rules={[
                  { required: true, message: "Please select date range" },
                ]}
              >
                <RangePicker
                  style={{ width: "100%" }}
                  disabledDate={(current) =>
                    current && current < moment().startOf("day")
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="timeDistribution"
                label="Time Distribution"
                initialValue="evenly"
              >
                <Select>
                  <Option value="evenly">Evenly Distributed</Option>
                  <Option value="random">Random Times</Option>
                  <Option value="business_hours">Business Hours Only</Option>
                  <Option value="custom">Custom Interval</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Show custom interval field when custom time distribution is selected */}
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.timeDistribution !== currentValues.timeDistribution
            }
          >
            {({ getFieldValue }) => {
              return getFieldValue("timeDistribution") === "custom" ? (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="customInterval"
                      label="Interval Between Pages (minutes)"
                      rules={[
                        { required: true, message: "Please enter interval" },
                        { type: "number", min: 1, message: "Minimum 1 minute" },
                      ]}
                      help="Pages will be scheduled with this many minutes between each"
                    >
                      <InputNumber
                        min={1}
                        style={{ width: "100%" }}
                        placeholder="e.g., 1 for 1 page per minute"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ) : null;
            }}
          </Form.Item>

          <Tabs defaultActiveKey="filters">
            <TabPane tab="Filters" key="filters">
              <Form.Item
                name={["filters", "excludeExisting"]}
                valuePropName="checked"
                initialValue={true}
              >
                <Checkbox>Exclude existing pages</Checkbox>
              </Form.Item>

              <Form.Item
                name={["filters", "dataSelection"]}
                label="Select Data Sources"
                help="Choose specific competitors, tools, locations, or use cases for your campaign. Leave empty to use all available data."
              >
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.pageType !== currentValues.pageType
                  }
                >
                  {({ getFieldValue, setFieldsValue }) => {
                    const pageType = getFieldValue("pageType");

                    return (
                      <CampaignDataSelector
                        pageType={pageType}
                        value={
                          getFieldValue(["filters", "dataSelection"]) || {}
                        }
                        onChange={(value) => {
                          setFieldsValue({
                            filters: {
                              ...getFieldValue("filters"),
                              dataSelection: value,
                            },
                          });
                        }}
                      />
                    );
                  }}
                </Form.Item>
              </Form.Item>
            </TabPane>

            <TabPane tab="AI Settings" key="ai">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={["aiProvider", "preferredProvider"]}
                    label="AI Provider"
                    initialValue="deepseek"
                  >
                    <Select>
                      <Option value="deepseek">DeepSeek (Cheapest)</Option>
                      <Option value="claude">Claude (Balanced)</Option>
                      <Option value="openai">OpenAI (Premium)</Option>
                      <Option value="template">Template Only</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="minQualityScore"
                    label="Minimum Quality Score"
                    initialValue={70}
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      style={{ width: "100%" }}
                      addonAfter="%"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={["aiProvider", "enableAI"]}
                    valuePropName="checked"
                    initialValue={true}
                  >
                    <Checkbox>Enable AI content generation</Checkbox>
                  </Form.Item>
                </Col>
              </Row>
            </TabPane>
          </Tabs>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Create Campaign
              </Button>
              <Button
                onClick={() => {
                  setCreateModalVisible(false);
                  createForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Campaign Modal */}
      <Modal
        title="Edit Campaign"
        visible={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
          setSelectedCampaign(null);
        }}
        footer={null}
        width={800}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdateCampaign}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Campaign Name"
                rules={[
                  { required: true, message: "Please enter campaign name" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="totalPages" label="Total Pages">
                <InputNumber min={1} max={10000} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="dateRange" label="Schedule Period">
                <RangePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="pagesPerBatch" label="Pages per Batch">
                <InputNumber min={1} max={100} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="timeDistribution" label="Time Distribution">
                <Select>
                  <Option value="evenly">Evenly Distributed</Option>
                  <Option value="random">Random Times</Option>
                  <Option value="business_hours">Business Hours Only</Option>
                  <Option value="custom">Custom Interval</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Show custom interval field when custom time distribution is selected */}
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.timeDistribution !== currentValues.timeDistribution
            }
          >
            {({ getFieldValue }) => {
              return getFieldValue("timeDistribution") === "custom" ? (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="customInterval"
                      label="Interval Between Pages (minutes)"
                      rules={[
                        { required: true, message: "Please enter interval" },
                        { type: "number", min: 1, message: "Minimum 1 minute" },
                      ]}
                      help="Pages will be scheduled with this many minutes between each"
                    >
                      <InputNumber
                        min={1}
                        style={{ width: "100%" }}
                        placeholder="e.g., 1 for 1 page per minute"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ) : null;
            }}
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Update Campaign
              </Button>
              <Button
                onClick={() => {
                  setEditModalVisible(false);
                  editForm.resetFields();
                  setSelectedCampaign(null);
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Progress Modal */}
      <Modal
        title={`Campaign Progress: ${selectedCampaign?.name}`}
        visible={progressModalVisible}
        onCancel={() => {
          setProgressModalVisible(false);
          setCampaignProgress(null);
          setSelectedCampaign(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setProgressModalVisible(false);
              setCampaignProgress(null);
              setSelectedCampaign(null);
            }}
          >
            Close
          </Button>,
        ]}
        width={600}
      >
        {campaignProgress && (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Row gutter={16}>
              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title="Total Progress"
                    value={campaignProgress.progress}
                    suffix="%"
                    prefix={
                      <Progress
                        type="circle"
                        percent={campaignProgress.progress}
                        width={50}
                      />
                    }
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title="Pages Generated"
                    value={campaignProgress.pagesGenerated}
                    suffix={`/ ${campaignProgress.totalPages}`}
                  />
                </Card>
              </Col>
            </Row>

            {campaignProgress.queueStats && (
              <Card title="Queue Status" size="small">
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic
                      title="Pending"
                      value={campaignProgress.queueStats.pending || 0}
                      valueStyle={{ fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Processing"
                      value={campaignProgress.queueStats.processing || 0}
                      valueStyle={{ fontSize: "16px", color: "#1890ff" }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Completed"
                      value={campaignProgress.queueStats.completed || 0}
                      valueStyle={{ fontSize: "16px", color: "#52c41a" }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Failed"
                      value={campaignProgress.queueStats.failed || 0}
                      valueStyle={{ fontSize: "16px", color: "#ff4d4f" }}
                    />
                  </Col>
                </Row>
              </Card>
            )}

            {campaignProgress.estimatedCompletion && (
              <Card title="Estimated Completion" size="small">
                <p>
                  <ClockCircleOutlined style={{ marginRight: 8 }} />
                  {moment(campaignProgress.estimatedCompletion).format(
                    "MMM DD, YYYY HH:mm"
                  )}
                </p>
              </Card>
            )}

            {campaignProgress.metrics && (
              <Card title="Performance Metrics" size="small">
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="Avg Quality"
                      value={campaignProgress.metrics.avgQualityScore}
                      precision={1}
                      suffix="%"
                      valueStyle={{ fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Success Rate"
                      value={campaignProgress.metrics.successRate}
                      precision={1}
                      suffix="%"
                      valueStyle={{ fontSize: "16px" }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Avg Gen Time"
                      value={campaignProgress.metrics.avgGenerationTime / 1000}
                      precision={1}
                      suffix="s"
                      valueStyle={{ fontSize: "16px" }}
                    />
                  </Col>
                </Row>
              </Card>
            )}
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default ProgrammaticSeoCampaigns;
